import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Flex, HStack, IconButton, useDisclosure, Stack, Fade, Link } from "@chakra-ui/react";
import { HamburgerIcon, CloseIcon } from "@chakra-ui/icons";
import LogoHeader from "../atoms/Logo";
import Overlay from "../atoms/Overlay";

export default function HeaderMenu() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isSticky, setIsSticky] = useState(false);
  const [currentSection, setCurrentSection] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  // ── Behavior Layer: Intro fade-in (home page ONLY) ──────────────────
  // On home page: header starts invisible and fades in after 4s to wait
  // for the intro animation. On all other pages: immediately visible.
  const [headerOpacity, setHeaderOpacity] = useState(isHomePage ? 0 : 1);

  useEffect(() => {
    if (isHomePage) {
      const timer = setTimeout(() => setHeaderOpacity(1), 4000);
      return () => clearTimeout(timer);
    }
  }, [isHomePage]);
  // ────────────────────────────────────────────────────────────────────

  useEffect(() => {
    const scrollContainer = document.querySelector(".scroll-container");

    const handleScroll = () => {
      if (scrollContainer) {
        setIsSticky(scrollContainer.scrollTop > 0);
      }
    };

    handleScroll();

    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
      return () => {
        scrollContainer.removeEventListener("scroll", handleScroll);
      };
    }
  }, []);

  useEffect(() => {
    const sections = [
      "hero-section",
      "aboutme",
      "skills",
      "testimonials",
      "projects",
      "journey",
      "contact",
      "footer",
      "cv-page",
    ];
    const observers = sections.map((id) => {
      const element = document.getElementById(id);
      if (!element) return null;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setCurrentSection(id);
          }
        },
        { threshold: 0.5, root: document.querySelector(".scroll-container") }
      );
      observer.observe(element);
      return observer;
    });
    return () => {
      observers.forEach((obs) => obs?.disconnect());
    };
  }, []);

  const Links = [
    { name: "About Me", id: "aboutme" },
    { name: "Skills", id: "skills" },
    { name: "Projects", id: "projects" },
    { name: "My Journey", id: "journey" },
    { name: "Contact", id: "contact" },
    // { name: "Resume", id: "cv-page", route: "/resume" },
  ];

  const handleScrollToSection = (link) => {
    if (link.route) {
      // For route-based links (like Resume), navigate to the page
      navigate(link.route);
    } else if (isHomePage) {
      // On home page, scroll to the section
      const element = document.getElementById(link.id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      // Not on home page and not a route link - go back to home
      navigate("/");
      // Scroll after navigation (delay to allow DOM to render)
      setTimeout(() => {
        const element = document.getElementById(link.id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 300);
    }
  };

  const useRealColors = ["aboutme", "skills", "testimonials", "contact", "footer"].includes(
    currentSection
  );

  return (
    <>
      {isOpen ? <Overlay /> : ""}
      <Flex
        w={"full"}
        position={"fixed"}
        top={isSticky ? "10px" : 0}
        transition="top 0.3s ease, opacity 0.5s ease"
        zIndex={99}
        opacity={headerOpacity}
        pointerEvents="none"
      >
        <Flex
          bg={isSticky ? "#00000020" : "transparent"}
          mx={isSticky ? "32px" : "0"}
          backdropFilter={isSticky ? "blur(10px)" : "none"}
          px={4}
          h={16}
          alignItems={"center"}
          w={"100%"}
          justify={"space-between"}
          transition="all 0.3s ease"
          rounded={isSticky ? "15px" : "0"}
          pointerEvents="auto"
        >
          <HStack spacing={8} w={"100%"} justify={{ base: "flex-start", md: "space-between" }}>
            <Box>
              <LogoHeader isSticky={isSticky} useRealColors={useRealColors} />
            </Box>

            <HStack
              as={"nav"}
              justifyContent={"end"}
              spacing={4}
              display={{ base: "none", md: "flex" }}
              zIndex={99}
            >
              {Links.map((link) => (
                <Link
                  px={2}
                  key={link.name}
                  onClick={() => handleScrollToSection(link)}
                  textDecoration={"none"}
                  color={isSticky ? (useRealColors ? "#000000" : "#ffffff90") : "white"}
                  _hover={{ color: "#DD9331", transform: "scale(1.1)" }}
                  transition="transform 0.3s ease"
                >
                  {link.name}
                </Link>
              ))}
            </HStack>
          </HStack>
          <IconButton
            fontSize="30px"
            color={isSticky ? (useRealColors ? "#000000" : "#ffffff90") : "white"}
            backgroundColor={"transparent"}
            _hover={{
              backgroundColor: "#DD9331",
              color: "#ffffff",
            }}
            icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
            aria-label={"Open Menu"}
            display={{ base: "flex", md: "none" }}
            onClick={isOpen ? onClose : onOpen}
            zIndex={2}
            transition="all 0.3s ease"
          />
        </Flex>

        {isOpen ? (
          <>
            <Fade in={isOpen}>
              <Box
                fontSize={"3xl"}
                mt={2}
                py={10}
                rounded={"15px"}
                position={"fixed"}
                top="80px"
                left="20px"
                right="20px"
                zIndex={9999}
                bg={"#F9F0DF"}
                display={{ base: "flex", md: "none" }}
                justifyContent={"center"}
                alignItems={"center"}
                flexDirection={"column"}
                boxShadow="lg"
              >
                <Stack as={"nav"} spacing={4} align={"center"} w="full">
                  {Links.map((link) => (
                    <Link
                      px={2}
                      key={link.name}
                      onClick={() => {
                        handleScrollToSection(link);
                        onClose(); // Close menu when link is clicked
                      }}
                      color="#115945"
                      fontWeight="bold"
                      _hover={{ color: "#DD9331" }}
                      transition="color 0.3s ease"
                      cursor="pointer"
                    >
                      {link.name}
                    </Link>
                  ))}
                </Stack>
              </Box>
            </Fade>
          </>
        ) : null}
      </Flex>
    </>
  );
}
